import Vue from 'vue';
import colorsApi from '@/api/colors';
import { findSize } from '@/support/constants';
import dayjs from 'dayjs';
import storage from '@/support/storage';
import { requiresPrescription } from '@/support/product';

const initialState = {
  showJobEditModal: false,
  selectedJobData: null,
  activeJobEditStep: 1,
  selectedFrameData: {
    shape: null,
    frameSize: null,
    leftArmSize: null,
    rightArmSize: null,
    frameColor: null,
    leftArmColor: null,
    rightArmColor: null,
    isExistingFrame: false,
  },
  selectedPrescription: null,
  selectedPrescriptionType: null,
  selectedPd: null,
  selectedLens: null,
  selectedLensCoating: null,
  colors: null,

  defaultFrameData: {
    frameSize: null,
    shape: null,
    leftArmSize: null,
    rightArmSize: null,
    frameColor: null,
    leftArmColor: null,
    rightArmColor: null,
  },
};

const getters = {
  isPrescriptionRequired: state =>
    requiresPrescription(state.selectedJobData?.item?.region_product),
  selectedLensesType: state =>
    state.selectedJobData?.item?.region_product?.product?.type?.lenses_type,
  selectedPrescriptionCategory: state => state.selectedPrescription?.category,
  selectedPrescriptionType: state => state.selectedPrescriptionType,
};

const actions = {
  resetAllSizes({ commit }) {
    commit('SET_ALL_SIZES', null);
  },
  resetAllColors({ commit }) {
    commit('SET_ALL_COLORS', null);
  },
  resetSizesAndColors({ commit }) {
    commit('defaultSizeAndColor');
  },
  resetPrescription({ commit }) {
    commit('SET_SELECTED_PRESCRIPTION', null);
    commit('SET_SELECTED_PRESCRIPTION_TYPE', null);
  },
  resetJobEditModule({ commit, dispatch }) {
    dispatch('resetSizesAndColors');
    dispatch('resetPrescription');
    commit('SET_SELECTED_PD', null);
    commit('SET_SELECTED_LENS', null);
  },
};

const mutations = {
  HIDE_JOB_EDIT_MODAL(state) {
    state.showJobEditModal = false;
    document.body.classList.remove('block-scroll');
  },
  SET_SELECTED_JOB(state, payload) {
    state.selectedJobData = payload;
    state.showJobEditModal = true;
    document.body.classList.add('block-scroll');
  },
  SET_ACTIVE_JOB_EDIT_STEP(state, payload) {
    state.activeJobEditStep = payload;
  },
  SET_ALL_SIZES(state, payload) {
    state.selectedFrameData.frameSize = payload;
    state.selectedFrameData.leftArmSize = payload;
    state.selectedFrameData.rightArmSize = payload;
  },
  async GET_ALL_COLORS(state) {
    // the stored copy only covers the wait; a week-old list hid colours that were changed since
    state.colors = storage.get('colors') || state.colors;
    const response = await colorsApi.getAllColors();
    state.colors = response.data.data;
    storage.set('colors', state.colors, dayjs().add(1, 'week').valueOf());
  },
  SET_ALL_COLORS(state, payload) {
    state.selectedFrameData.frameColor = payload;
    state.selectedFrameData.leftArmColor = payload;
    state.selectedFrameData.rightArmColor = payload;
  },
  SET_ARM_SIZE(state, payload) {
    state.selectedFrameData.leftArmSize = payload;
    state.selectedFrameData.rightArmSize = payload;
  },
  SET_ARM_LEFT_SIZE(state, payload) {
    state.selectedFrameData.leftArmSize = payload;
  },
  SET_ARM_RIGHT_SIZE(state, payload) {
    state.selectedFrameData.rightArmSize = payload;
  },
  SET_SHAPE(state, payload) {
    state.selectedFrameData.shape = payload;
  },
  SET_ARM_COLOR(state, payload) {
    state.selectedFrameData.leftArmColor = payload;
    state.selectedFrameData.rightArmColor = payload;
  },
  SET_ARM_RIGHT_COLOR(state, payload) {
    state.selectedFrameData.rightArmColor = payload;
  },
  SET_ARM_LEFT_COLOR(state, payload) {
    state.selectedFrameData.leftArmColor = payload;
  },
  SET_FRAME_COLOR(state, payload) {
    state.selectedFrameData.frameColor = payload;
  },
  SET_SELECTED_PRESCRIPTION(state, payload) {
    state.selectedPrescription = payload;
  },
  SET_SELECTED_PRESCRIPTION_TYPE(state, payload) {
    state.selectedPrescriptionType = payload;
  },
  SET_SELECTED_PD(state, payload) {
    state.selectedPd = payload;
  },
  SET_SELECTED_LENS(state, payload) {
    state.selectedLens = payload;
  },
  SET_SELECTED_LENS_MOD(state, mod) {
    if (state.selectedLens) {
      Vue.set(state.selectedLens, 'selected_lens_mod', mod);
    }
  },
  SET_SELECTED_LENS_MOD_CORRIDOR(state, mod) {
    if (state.selectedLens) {
      Vue.set(state.selectedLens, 'selected_lens_mod_corridor', mod);
    }
  },
  SET_SELECTED_LENSCOATING(state, payload) {
    state.selectedLensCoating = payload;
  },
  SET_IS_EXISTING_DRESDEN(state, payload) {
    state.selectedFrameData.isExistingDresden = payload;
  },
  SET_JOB_EDIT_MODAL_DATA(state, payload) {
    state.selectedFrameData.frameColor = payload.frame?.color?.id || null;
    state.selectedFrameData.rightArmColor = payload.rightArm?.color?.id || null;
    state.selectedFrameData.leftArmColor = payload.leftArm?.color?.id || null;
    state.selectedFrameData.shape = payload.frame?.shape || null;
    state.defaultFrameData.shape = payload.frame?.shape || null;
    state.defaultFrameData.frameColor = payload.frame?.color?.id || null;
    state.defaultFrameData.rightArmColor = payload.rightArm?.color?.id || null;
    state.defaultFrameData.leftArmColor = payload.leftArm?.color?.id || null;

    state.defaultFrameData.isExistingDresden = payload.frame?.existingdresden || false;

    // A part saved without a known size (e.g. an arm with only a colour) stays empty for staff to
    // pick; it used to throw here and stop the modal from loading the lens type and PDs.
    const selectedFrameSize = findSize(payload.frame?.sizeId)?.value ?? null;
    const selectedleftArmSize = findSize(payload.leftArm?.sizeId)?.value ?? null;
    const selectedRightArmSize = findSize(payload.rightArm?.sizeId)?.value ?? null;

    state.selectedFrameData.frameSize = selectedFrameSize;
    state.selectedFrameData.leftArmSize = selectedleftArmSize;
    state.selectedFrameData.rightArmSize = selectedRightArmSize;

    state.defaultFrameData.frameSize = selectedFrameSize;
    state.defaultFrameData.leftArmSize = selectedleftArmSize;
    state.defaultFrameData.rightArmSize = selectedRightArmSize;

    state.selectedPrescription = payload.prescription ? payload.prescription.detail : null;
    state.selectedPd = payload.item.additional_info.data.pd
      ? {
          detail: payload.item.additional_info.data.pd.detail,
          pdPresenter: payload.item.additional_info.pdPresenter,
        }
      : null;
    state.selectedLens = payload.lenses ? payload.lenses : null;
    const savedMod = payload.item?.additional_info?.data?.selected_lens_mod;
    if (state.selectedLens && savedMod) {
      Vue.set(state.selectedLens, 'selected_lens_mod', savedMod);
    }
    const savedCorridorMod = payload.item?.additional_info?.data?.selected_lens_mod_corridor;
    if (state.selectedLens && savedCorridorMod) {
      Vue.set(state.selectedLens, 'selected_lens_mod_corridor', savedCorridorMod);
    }
    state.selectedPrescriptionType = payload.prescription?.typeId
      ? payload.prescription.typeId
      : null;


    if (selectedFrameSize === 'nd') {
      state.selectedFrameData.shape = 'non-dresden';
      state.defaultFrameData.shape = 'non-dresden';
    }
  },
  defaultSizeAndColor(state) {
    state.selectedFrameData.frameSize = state.defaultFrameData.frameSize;
    state.selectedFrameData.leftArmSize = state.defaultFrameData.leftArmSize;
    state.selectedFrameData.rightArmSize = state.defaultFrameData.rightArmSize;
    state.selectedFrameData.frameColor = state.defaultFrameData.frameColor;
    state.selectedFrameData.leftArmColor = state.defaultFrameData.leftArmColor;
    state.selectedFrameData.rightArmColor = state.defaultFrameData.rightArmColor;

    state.selectedFrameData.shape = state.defaultFrameData.shape;

    if (state.defaultFrameData.frameSize === 'Non-Dresden') {
      state.selectedFrameData.shape = 'non-dresden';
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
